import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, Mail, CalendarDays, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { SITE_SLUGS, parsePath } from '../utils/siteNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PHONE = '+382 68 188 816';
const WHATSAPP = 'https://wa.me/38268188816';
const EMAIL = 'umapremiumul@gmail.com';
const BOOK_URL = 'https://beds24.com/book-online.php?propid=327426&layout=1&colorbody=ffffff&colorpanel=f8fafc&colorcontent=1e293b&colorlink=d97706&colorbuttonbg=d97706&colorbuttontext=ffffff';

const T = {
  en: { title: "We'll be back shortly", body: 'Our website is taking a short break. Reception is happy to help you by phone, WhatsApp or e-mail.', back: 'We expect to be back online on', call: 'Call reception', wa: 'WhatsApp', mail: 'E-mail us', book: 'Book a room', thanks: 'Thank you for your patience — the Uma Premium team' },
  me: { title: 'Uskoro se vraćamo', body: 'Naš sajt je na kratkoj pauzi. Recepcija vam rado pomaže telefonom, WhatsApp-om ili e-mailom.', back: 'Očekujemo povratak', call: 'Pozovite recepciju', wa: 'WhatsApp', mail: 'Pišite nam', book: 'Rezervišite sobu', thanks: 'Hvala na strpljenju — tim Uma Premium' },
  sq: { title: 'Kthehemi së shpejti', body: 'Faqja jonë është në një pushim të shkurtër. Recepsioni ju ndihmon me kënaqësi me telefon, WhatsApp ose e-mail.', back: 'Presim të jemi sërish online më', call: 'Telefononi recepsionin', wa: 'WhatsApp', mail: 'Na shkruani', book: 'Rezervoni dhomë', thanks: 'Faleminderit për durimin — ekipi Uma Premium' },
  de: { title: 'Wir sind bald zurück', body: 'Unsere Website macht eine kurze Pause. Die Rezeption hilft Ihnen gern per Telefon, WhatsApp oder E-Mail.', back: 'Voraussichtlich wieder online am', call: 'Rezeption anrufen', wa: 'WhatsApp', mail: 'E-Mail schreiben', book: 'Zimmer buchen', thanks: 'Vielen Dank für Ihre Geduld — Ihr Uma Premium Team' },
  pl: { title: 'Wracamy wkrótce', body: 'Nasza strona ma krótką przerwę. Recepcja chętnie pomoże telefonicznie, przez WhatsApp lub e-mail.', back: 'Planowany powrót online', call: 'Zadzwoń do recepcji', wa: 'WhatsApp', mail: 'Napisz do nas', book: 'Zarezerwuj pokój', thanks: 'Dziękujemy za cierpliwość — zespół Uma Premium' },
  ru: { title: 'Мы скоро вернёмся', body: 'Наш сайт на короткой паузе. Ресепшн с радостью поможет вам по телефону, в WhatsApp или по e-mail.', back: 'Планируем вернуться', call: 'Позвонить на ресепшн', wa: 'WhatsApp', mail: 'Написать нам', book: 'Забронировать номер', thanks: 'Спасибо за терпение — команда Uma Premium' },
  hu: { title: 'Hamarosan visszatérünk', body: 'Weboldalunk rövid szünetet tart. A recepció örömmel segít telefonon, WhatsAppon vagy e-mailben.', back: 'Várhatóan újra elérhető', call: 'Recepció hívása', wa: 'WhatsApp', mail: 'Írjon nekünk', book: 'Szobafoglalás', thanks: 'Köszönjük türelmét — az Uma Premium csapata' },
  ro: { title: 'Revenim în curând', body: 'Site-ul nostru ia o scurtă pauză. Recepția vă ajută cu plăcere prin telefon, WhatsApp sau e-mail.', back: 'Estimăm revenirea online pe', call: 'Sunați la recepție', wa: 'WhatsApp', mail: 'Scrieți-ne', book: 'Rezervați o cameră', thanks: 'Vă mulțumim pentru răbdare — echipa Uma Premium' },
};
const LOCALE = { en: 'en-GB', me: 'sr-Latn-ME', sq: 'sq-AL', de: 'de-DE', pl: 'pl-PL', ru: 'ru-RU', hu: 'hu-HU', ro: 'ro-RO' };

const isPublicPath = (pathname) => {
  const { lang, slug } = parsePath(pathname);
  return slug === '' ? (pathname === '/' || lang !== 'en') : SITE_SLUGS.includes(slug);
};

const Action = ({ href, icon: Icon, label, primary, testId }) => (
  <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" data-testid={testId}
    className={`inline-flex items-center gap-2 px-5 h-12 rounded-full text-sm font-semibold transition-transform hover:-translate-y-0.5 ${primary ? 'bg-amber-500 text-slate-950 hover:bg-amber-400' : 'bg-white/10 text-white hover:bg-white/15 border border-white/15'}`}>
    <Icon className="w-4 h-4" /> {label}
  </a>
);

export const MaintenanceOverlay = () => {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/site-status`);
        if (r.ok && !cancelled) setStatus(await r.json());
      } catch (e) { console.warn('[site-status] check failed:', e); }
    };
    check();
    const id = setInterval(check, 120000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const active = !!status?.maintenance && isPublicPath(pathname);
  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);

  if (!active) return null;
  const t = T[language] || T.en;
  const backOn = status.back_on ? new Date(`${status.back_on}T12:00:00`).toLocaleDateString(LOCALE[language] || 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  return (
    <div className="fixed inset-0 z-[200] bg-slate-950 text-white overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="maintenance-title" data-testid="maintenance-overlay">
      <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_20%_20%,#f59e0b,transparent_40%),radial-gradient(circle_at_80%_70%,#f59e0b,transparent_35%)]" aria-hidden="true" />
      <div className="relative min-h-full flex items-center justify-center px-6 py-16">
        <div className="max-w-xl w-full">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl bg-amber-500 text-slate-950 font-bold flex items-center justify-center text-lg tracking-tight">UP</div>
            <div>
              <p className="font-serif text-xl leading-tight">Uma Premium</p>
              <p className="text-amber-400 text-xs tracking-[0.3em] uppercase">Ulcinj · ★★★★</p>
            </div>
          </div>
          <h2 id="maintenance-title" className="font-serif text-4xl sm:text-5xl leading-tight mb-5" data-testid="maintenance-title">{t.title}</h2>
          <p className="text-slate-300 text-base sm:text-lg leading-relaxed">{t.body}</p>
          {status.message && <p className="mt-4 text-white/90 text-base leading-relaxed border-l-2 border-amber-500 pl-4 whitespace-pre-wrap" data-testid="maintenance-message">{status.message}</p>}
          {backOn && <p className="mt-5 inline-flex items-center gap-2 text-amber-300 text-sm" data-testid="maintenance-back-on"><CalendarDays className="w-4 h-4" /> {t.back} <b>{backOn}</b></p>}
          <div className="mt-10 flex flex-wrap gap-3">
            <Action href={`tel:${PHONE.replace(/\s/g, '')}`} icon={Phone} label={`${t.call} · ${PHONE}`} primary testId="maintenance-call" />
            <Action href={WHATSAPP} icon={MessageCircle} label={t.wa} testId="maintenance-whatsapp" />
            <Action href={`mailto:${EMAIL}`} icon={Mail} label={t.mail} testId="maintenance-email" />
            {status.show_booking && <Action href={BOOK_URL} icon={CalendarDays} label={t.book} testId="maintenance-book" />}
          </div>
          <p className="mt-12 text-slate-500 text-sm">{t.thanks}</p>
        </div>
      </div>
    </div>
  );
};
